import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Subscription, lastValueFrom, map } from 'rxjs';
import {
  PageableFilter,
  PageableRequest,
  PaymentRoundControllerService
} from 'src/app/admin-api';
import { PaymentRoundCreditCardPayment } from 'src/app/admin-api/model/paymentRoundCreditCardPayment';
import { SubscriberPaymentStepError } from 'src/app/admin-api/model/subscriberPaymentStepError';
import { SubscriberPaymentStepErrorSummary } from 'src/app/admin-api/model/subscriberPaymentStepErrorSummary';
import { PageContent, TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface ErrorSummaryRow {
  message: string;
  total: number;
}

@Component({
  selector: 'app-payment-round-error-report',
  templateUrl: './payment-round-error-report.component.html',
  styleUrls: ['./payment-round-error-report.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PaymentRoundErrorReportComponent implements OnInit, OnDestroy {
  public paymentRoundService = inject(PaymentRoundControllerService);
  private config = inject(DynamicDialogConfig, { optional: true });

  @Input() paymentRoundId: number | undefined;

  activeTab = 0;
  errorSummaryRows: ErrorSummaryRow[] = [];
  fixedFilters: PageableFilter[] = [];
  payments: PaymentRoundCreditCardPayment[] = [];
  paymentsLoaded = false;
  paymentsLoading = false;
  localLoading = false;

  private loaderSubscription: Subscription | undefined;
  private suppressGlobalLoaderHide = false;

  errorCols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Id assinatura')
      .setField('subscriberId')
      .setType('number')
      .setRouterLink('/users/subscribers/')
      .setRouterLinkFieldName('subscriberId')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('E-mail')
      .setField('email')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status assinante')
      .setField('subscriberStatus')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Box ID')
      .setField('boxId')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Erro')
      .setField('lastMessage')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status pagamento')
      .setField('transactionStatusName')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo erro')
      .setField('paymentResponseMessageType')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('LR')
      .setField('lr')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('TID')
      .setField('tid')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Extra')
      .setField('extraData')
      .setCondition('contains')
      .build()
  ];

  paymentCols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Id pagamento')
      .setField('creditCardPaymentId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Id assinatura')
      .setField('subscriberId')
      .setType('number')
      .setRouterLink('/users/subscribers/')
      .setRouterLinkFieldName('subscriberId')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Plano')
      .setField('subscriptionName')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('E-mail')
      .setField('email')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status assinatura')
      .setField('subscriberStatus')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Gateway')
      .setField('gatewayName')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('transactionStatusName')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo erro')
      .setField('paymentResponseMessageType')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('LR')
      .setField('lr')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('TID')
      .setField('tid')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data criacão')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data autorizacão')
      .setField('dateAuthorized')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Extra')
      .setField('extraData')
      .setCondition('contains')
      .build()
  ];

  findErrorsPage = async (
    request: PageableRequest
  ): Promise<PageContent<SubscriberPaymentStepError>> => {
    return lastValueFrom(
      this.paymentRoundService
        .getPaymentRoundErrors(request)
        .pipe(
          map((data) => data.result as PageContent<SubscriberPaymentStepError>)
        )
    );
  };

  async ngOnInit(): Promise<void> {
    this.setupLocalLoaderRedirect();

    const roundId = this.paymentRoundId ?? this.config?.data?.paymentRoundId;
    if (!roundId) {
      return;
    }

    this.paymentRoundId = roundId;
    this.fixedFilters = [
      {
        condition: 'equals',
        field: 'paymentRoundId',
        fieldType: 'number',
        value: roundId
      }
    ];

    LoaderService.showLoader();
    try {
      await this.loadErrorSummary();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  ngOnDestroy(): void {
    this.loaderSubscription?.unsubscribe();
    this.localLoading = false;
  }

  async onTabChange(tabIndex: number): Promise<void> {
    this.activeTab = tabIndex;
    if (tabIndex === 1 && !this.paymentsLoaded) {
      await this.loadPayments();
    }
  }

  private setupLocalLoaderRedirect(): void {
    this.loaderSubscription = LoaderService.progressSubscriber.subscribe(
      (show) => {
        if (show) {
          this.localLoading = true;
          this.suppressGlobalLoaderHide = true;
          LoaderService.showLoader(false);
          this.suppressGlobalLoaderHide = false;
          return;
        }

        if (!this.suppressGlobalLoaderHide) {
          this.localLoading = false;
        }
      }
    );
  }

  private async loadErrorSummary(): Promise<void> {
    const result = await lastValueFrom(
      this.paymentRoundService
        .getPaymentRoundErrorsSummaryById(this.paymentRoundId as number)
        .pipe(map((data) => data.result))
    );


    this.errorSummaryRows = result.reduce((list: ErrorSummaryRow[], s: SubscriberPaymentStepErrorSummary) => {
      const exists = list.find(l => l.message === s.error);
      if (exists)
        exists.total += s.total || 0;
      else
        list.push({ message: s.error, total: s.total || 0 });
      return list;
    }, []).sort((a1, a2) => a2.total - a1.total);
  }

  private async loadPayments(): Promise<void> {
    this.paymentsLoading = true;
    LoaderService.showLoader();
    try {
      this.payments = await lastValueFrom(
        this.paymentRoundService
          .getPaymentRoundPayments(this.paymentRoundId as number)
          .pipe(map((data) => data.result || []))
      );
      this.paymentsLoaded = true;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.paymentsLoading = false;
      LoaderService.showLoader(false);
    }
  }

  private normalizeErrorSummaries(
    data:
      | SubscriberPaymentStepErrorSummary
      | SubscriberPaymentStepErrorSummary[]
      | undefined
  ): SubscriberPaymentStepErrorSummary[] {
    if (!data) {
      return [];
    }

    if (Array.isArray(data)) {
      return data;
    }

    return [data];
  }
}
